import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonHeader, IonToolbar, IonTitle, IonContent, IonLabel, IonButton, IonIcon, ModalController, IonSkeletonText } from '@ionic/angular/standalone';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { Organisation, OrganisationService } from '../../../organisation';
import { OrganisationUserActions, OrganisationUserSelectors } from '../../../organisation-user';
import { DisplayService, OrganisationUserRoles } from '@qid/core';
import { AuthSelectors } from '../../../authentication';
import { OrganisationRelationshipFacade } from '../../services/organisation-relationship.facade';
import { AvatarRoundComponent, SuperButtonComponent, SuperInputComponent } from "@qid/super";
import { OrganisationRelationship } from '../../model/organisation-relationship.model';
import { CreateOrganisationRelationshipDto } from '../../dto/create-organisation-relationship.dto';
import { OrganisationRelationshipService } from '../../services/organisation-relationship.service';

@Component({
    selector: 'app-add-branch',
    templateUrl: './add-branch.page.html',
    styleUrls: ['./add-branch.page.scss'],
    standalone: true,
    imports: [
        CommonModule,
        IonHeader,
        IonToolbar,
        IonTitle,
        IonContent,
        AvatarRoundComponent,
        SuperButtonComponent,
        SuperInputComponent,
        IonButton,
        IonIcon,
        IonSkeletonText
    ]
})
export class AddBranchPage {

    public activeAccount$ = this.store.select(AuthSelectors.selectActiveAccount);

    public organisationRelationships$ = this.orgRelationshipsFacade.childOrganisations$;

    public parentOrganisation$ = this.orgRelationshipsFacade.parentBranchRealation$;
    public organisationsThatCanBeAdded: Organisation[] = null

    public orgName: string = '';

    public isLoading: boolean = false;

    constructor(private store: Store,
        private orgRelationshipsFacade: OrganisationRelationshipFacade,
        private modalController: ModalController,
        private organisationService: OrganisationService,
        private organisationRelationshipService: OrganisationRelationshipService,
        private displayService: DisplayService
    ) {

    }

    ionViewDidEnter() {
        this.setOrganisation();
    }

    async setOrganisation() {

        this.isLoading = true;
        try {
            this.isLoading = true;
            this.organisationsThatCanBeAdded = await this.getOrganisations();
        } catch (error) {
            this.displayService.toast({ message: 'Error fetching organisations', position: 'top' });
        } finally {
            this.isLoading = false;
        }
    }


    async setParentOrganisation(childOrgId: string, index: number) {


        const activeAccount: Organisation = await firstValueFrom(this.store.select(AuthSelectors.selectActiveAccount))

        const dto: CreateOrganisationRelationshipDto = {
            parentOrg: activeAccount._id,
            childOrg: childOrgId
        }
        const res = await this.organisationRelationshipService.create(dto)

        if (res && res.data) {
            index ? this.organisationsThatCanBeAdded.splice(index, 1) : null;
            this.orgRelationshipsFacade.refresh()
            this.modalController.dismiss();
            this.displayService.toast({ message: 'Branch added successfully', position: 'top' });
        }

        if (res.errorMessage) {
            this.displayService.toast({ message: res.errorMessage, position: 'bottom' });
        }
    }

    getSetParentOrganisationAction(childOrgId: string, index: number) {
        return () => this.setParentOrganisation(childOrgId, index);
    }

    async getOrganisations(): Promise<Organisation[]> {
        const state = await firstValueFrom(
            this.store.select(OrganisationUserSelectors.selectOrganisationUserState)
        );

        if (!state?.authenticatedUserOrganisations?.length) {
            return [];
        }

        const activeAccount = await firstValueFrom(this.activeAccount$)

        const allOrganisations = state.authenticatedUserOrganisations
            .filter(({ role, organisation }) =>
                (role >= OrganisationUserRoles.ADMIN && organisation._id !== activeAccount._id))

            .map(({ organisation }) => organisation);

        if (!allOrganisations.length) {
            return [];
        }

        const organisationRelationships = await firstValueFrom(this.organisationRelationships$);

        if (!organisationRelationships?.length) {
            return allOrganisations;
        }

        const alreadyAddedOrganisations = new Set(
            organisationRelationships.map(({ childOrg }) => childOrg._id)
        );

        const uniqueOrganisations = allOrganisations.filter(
            ({ _id }) => !alreadyAddedOrganisations.has(_id)
        );

        return uniqueOrganisations;
    }

    close() {
        this.modalController.dismiss();
    }

    createOrganisation = async () => {

        const parentOrg: OrganisationRelationship = await firstValueFrom(this.parentOrganisation$)
        const org = await this.organisationService.create({
            name: this.orgName,
            industry: parentOrg?.parentOrg?.industry?._id,
        })

        if (org) {
            this.store.dispatch(OrganisationUserActions.fetchUserOrganisations())
            await this.setParentOrganisation(org._id, null)
        }
    }

}

